import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Mail, Rocket } from "lucide-react";

export default function AdminLogin() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-grid-tech flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_top,rgba(255,136,0,0.08),transparent_55%)]" />
      <div className="glass relative w-full max-w-md rounded-2xl p-8 sm:p-10 shadow-[0_20px_80px_rgba(0,0,0,0.6)]">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#FF8800] to-[#ff5e00] flex items-center justify-center shadow-[0_0_30px_rgba(255,136,0,0.35)]">
            <Rocket className="w-5 h-5 text-[#06080E]" strokeWidth={2.4} />
          </div>
          <div>
            <p className="font-display font-bold text-xl tracking-wide text-white">LAUNCHLAB</p>
            <p className="font-mono-tech text-[10px] tracking-[0.3em] text-slate-400 uppercase">Admin Console</p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="admin-login-form">
          <div className="space-y-2">
            <Label htmlFor="admin-email" className="text-slate-300 text-sm">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                id="admin-email"
                data-testid="admin-login-email-input"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@launchlab.in"
                className="pl-10 bg-[#0D111D] border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-[#FF8800]"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-password" className="text-slate-300 text-sm">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                id="admin-password"
                data-testid="admin-login-password-input"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                className="pl-10 bg-[#0D111D] border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-[#FF8800]"
              />
            </div>
          </div>

          {error && (
            <div data-testid="admin-login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <Button
            type="submit"
            data-testid="admin-login-submit-button"
            disabled={loading}
            className="w-full bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold hover:opacity-90 transition-opacity h-11"
          >
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </form>

        <a href="/" data-testid="admin-back-to-site-link" className="block text-center mt-6 text-xs text-slate-500 hover:text-slate-300 transition-colors">
          ← Back to launchlab.in
        </a>
      </div>
    </div>
  );
}
